import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { UseQueryResult } from '@tanstack/react-query';
import { ReactElement, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { formatErrorMessages } from '@/utils/display-error-snackbar';

type QueryBoundaryProps<TData> = {
    query: UseQueryResult<TData>;
    children: (data: TData) => ReactNode;
    /** Title under the spinner. Defaults to `query_boundary.loading`. */
    loadingMessage?: string;
    /** Title shown instead of the raw error. Defaults to `query_boundary.error`. */
    errorMessage?: string;
    /** Title for the empty result. Defaults to `query_boundary.empty`. */
    emptyMessage?: string;
    isEmpty?: (data: TData) => boolean;
    /** Replaces the whole loading UI, `loadingMessage` included. */
    loadingFallback?: ReactNode;
    /** Replaces the whole empty UI, `emptyMessage` included. */
    emptyFallback?: ReactNode;
};

export function QueryBoundary<TData>({
    query,
    children,
    loadingMessage,
    errorMessage,
    emptyMessage,
    isEmpty,
    loadingFallback,
    emptyFallback,
}: QueryBoundaryProps<TData>): ReactElement {
    if (query.status === 'pending') {
        return (
            <>
                {loadingFallback ?? (
                    <QueryLoadingStatus
                        message={loadingMessage}
                        isDisabled={query.fetchStatus === 'idle'}
                    />
                )}
            </>
        );
    }

    if (query.status === 'error') {
        return (
            <QueryErrorStatus
                error={query.error}
                message={errorMessage}
                onRetry={query.refetch}
            />
        );
    }

    if (!query.data || isEmpty?.(query.data)) {
        return (
            <>
                {emptyFallback ?? (
                    <QueryEmptyStatus
                        message={emptyMessage}
                        onRetry={query.refetch}
                    />
                )}
            </>
        );
    }

    return <>{children(query.data)}</>;
}

const statusBoxSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1.5,
    mt: 4,
} as const;

const RetryButton = ({ onRetry }: { onRetry?: () => void }) => {
    const { t } = useTranslation();

    if (!onRetry) {
        return null;
    }

    // Wrapped so the click event is not passed on as `RefetchOptions`.
    return (
        <Button variant='outlined' onClick={() => onRetry()}>
            {t('common.try_again')}
        </Button>
    );
};

export const QueryLoadingStatus = ({
    message,
    isDisabled,
    testid,
}: {
    message?: string;
    isDisabled?: boolean;
    testid?: string;
}) => {
    const { t } = useTranslation();

    useEffect(() => {
        // A query that stays disabled never resolves, so the boundary would
        // spin forever. Gates like `enabled: !!routerParam` settle on the next
        // render; anything else is a wiring mistake worth seeing in the console.
        if (isDisabled) {
            console.error(
                '[QueryBoundary] the query is disabled (`enabled: false`) and will not resolve while it stays that way.',
            );
        }
    }, [isDisabled]);

    return (
        <Box sx={statusBoxSx} data-testid={testid ?? 'query-boundary-loading'}>
            <CircularProgress />
            <Typography>{message ?? t('query_boundary.loading')}</Typography>
        </Box>
    );
};

export const QueryErrorStatus = ({
    error,
    message,
    onRetry,
}: {
    error: unknown;
    message?: string;
    onRetry?: () => void;
}) => {
    const { t } = useTranslation();
    const title = message ?? t('query_boundary.error');

    useEffect(() => {
        // The API detail stays out of the UI — the user gets `title` instead.
        console.error(
            '[QueryBoundary]',
            title,
            formatErrorMessages(error),
            error,
        );
    }, [error, title]);

    return (
        <Box sx={statusBoxSx} data-testid='query-boundary-error'>
            <Typography color='error'>{title}</Typography>
            <RetryButton onRetry={onRetry} />
        </Box>
    );
};

export const QueryEmptyStatus = ({
    message,
    onRetry,
}: {
    message?: string;
    onRetry?: () => void;
}) => {
    const { t } = useTranslation();

    return (
        <Box sx={statusBoxSx} data-testid='query-boundary-empty'>
            <Typography>{message ?? t('query_boundary.empty')}</Typography>
            <RetryButton onRetry={onRetry} />
        </Box>
    );
};
